import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconChevronDown } from '@tabler/icons-react'

import Button from '@components/Button'
import Popup from '@components/Popup'
import RadioButton from '@components/RadioButton'

import { toast } from '@src/helpers/toaster'
import { useInviteUser } from '@queries/Teams'
import { requestErrorsHandler } from '@helpers/utils'
import { userRoleLabel } from '@src/constants'

import { OrgStructureRecord } from '@pages/Team/Members/types'
import { Role as UserRole } from '@type/common'

export interface NotInvitedCellProps {
  record: OrgStructureRecord
}

// Renders the "Not Invited" cell for org-structure rows where the row's
// person is in the team directory (e.g. synced from Slack) but does not yet
// have an Approveit account/invite. Clicking the cell opens a Popup that
// lets an admin pick a role and send the invite.
//
// Earlier versions of this UI rendered the radios + Invite button INSIDE a
// react-select Menu by overriding the Option component. That fought
// react-select's focus management and auto-placement on every state change,
// causing the popup to fly to the top-left of the page when interacting
// with the radios. Using a real modal here removes the entire class of bug.
export const NotInvitedCell: React.FC<NotInvitedCellProps> = ({ record }) => {
  const { t } = useTranslation(['common', 'org'])
  const [isOpen, setIsOpen] = useState(false)
  const [role, setRole] = useState<UserRole>(UserRole.requestor)
  const { mutateAsync: invite, isLoading: isInviting } = useInviteUser()

  const handleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleInvite = useCallback(async () => {
    try {
      const result = await invite({ emails: [record.email], role })
      if (result?.skipped_emails?.length) {
        toast.warning(t('common:invites.alreadyMember'))
      }
      setIsOpen(false)
    } catch (err) {
      requestErrorsHandler(err)
    }
  }, [invite, record.email, role, t])

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className="flex items-center gap-1 text-[#343A40] hover:opacity-80"
        data-qa="not-invited-cell"
      >
        <IconChevronDown stroke={1.25} className="w-5 h-5 text-slate-950" />
        <span>{userRoleLabel.notInvited}</span>
      </button>
      {isOpen && (
        <Popup
          title={t('org:rolePicker.invitePopupTitle', { defaultValue: 'Invite {{email}}', email: record.email })}
          onClose={handleClose}
          className="!max-w-sm"
        >
          <p className="text-grey_1 mb-3 text-sm">{t('common:userRoles.description.notInvited')}</p>
          <div className="flex flex-col gap-2 mb-4">
            <RadioButton
              checked={role === UserRole.admin}
              value={UserRole.admin}
              name="inviteRole"
              onChange={() => setRole(UserRole.admin)}
              title={`Invite as ${userRoleLabel.Admin}`}
            />
            <RadioButton
              checked={role === UserRole.requestor}
              value={UserRole.requestor}
              name="inviteRole"
              onChange={() => setRole(UserRole.requestor)}
              title={`Invite as ${userRoleLabel.Requestor}`}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button color="white" onClick={handleClose} disabled={isInviting}>
              {t('common:invites.cancelLabel')}
            </Button>
            <Button onClick={handleInvite} loading={isInviting}>
              {t('common:invites.proceedLabel')}
            </Button>
          </div>
        </Popup>
      )}
    </>
  )
}

export default React.memo(NotInvitedCell)
